/**
 * Загрузка файлов вложений на платформу (`POST /platform/upload`).
 *
 * Два режима: асинхронный (axios, UI) и синхронный (XHR) — для ответа `getData`/`getFullData`
 * по WebSocket без `await` в цепочке middleware executeScript.
 *
 * Upload вызывается из `ElementRenderer.uploadPendingAttachmentsForGetFullData`, не при выборе файла.
 *
 * Контракт REST (WUI-291): multipart `request` — JSON Blob (`application/json`),
 * `file` — тело; ответ — `application/json` (`UniData` как объект).
 *
 * @see dev/WUI-293.md — п. 7–9, 14–15
 */
import api from '@/common/constants/api'
import { IKey } from '@/common/constants/ikey'
import { axios as axiosInstance } from '@/common/plugins/axios'
import type { FileItem } from '@/common/types/fileItem'
import { applyWorkflowAuthHeaders, getPlatformUploadUrl } from '@/common/utils/workflowAuthHeaders'
import { getDsAttachmentStore } from '@/common/composables/useDsAttachmentState'
import {
  shouldPromoteAttachmentViaDsSnapshot,
} from '@/common/utils/attachmentRpcPolicy'
import {
  MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD,
  ATTACHMENT_STORAGE_URI_FIELD_KEYS,
  buildAttachmentR7PromoteUploadRequest,
  buildAttachmentUploadRequest,
  getAttachmentStorageUri,
  getScalarFromFields,
  parseAttachmentUploadResponse,
  unwrapPlatformJsonPayload,
} from '@/common/utils/fileAttachmentsUniData'
import {
  getMiddleErrorMessage,
  getNonUniDataUploadResponseMessage,
  isMiddleErrorResponse,
  isUniDataResponseDocument,
} from '@/common/utils/middleErrorResponse'
import type { PlatformFileSessionContext } from '@/common/utils/platformFileSession'

const UPLOAD_TIMEOUT_MS = 300_000

export type AttachmentUploadSyncMode = 'r7_promote' | 'file'

/** Выбор режима sync-upload перед getFullData (unit-тестируемый роутинг). */
export function resolveAttachmentUploadSyncMode(
  item: FileItem,
  elementId: number,
): AttachmentUploadSyncMode {
  if (!Number.isFinite(elementId) || elementId <= 0) return 'file'
  if (shouldPromoteAttachmentViaDsSnapshot(item, elementId)) {
    return 'r7_promote'
  }
  return 'file'
}

/** Multipart для `POST /platform/upload` (WUI-291: `@RequestPart` + Jackson на middleware). */
export function appendAttachmentUploadMultipart(
  formData: FormData,
  request: Record<string, unknown>,
  file: File,
): void {
  formData.append(
    'request',
    new Blob([JSON.stringify(request)], { type: 'application/json' }),
  )
  formData.append('file', file, file.name)
}

/** Ошибка HTTP upload вложения (статус, 413, таймаут). */
export class AttachmentUploadError extends Error {
  readonly status?: number
  readonly isPayloadTooLarge: boolean
  readonly isTimeout: boolean

  constructor(
    message: string,
    options?: { status?: number; isPayloadTooLarge?: boolean; isTimeout?: boolean },
  ) {
    super(message)
    this.name = 'AttachmentUploadError'
    this.status = options?.status
    this.isPayloadTooLarge = options?.isPayloadTooLarge === true
    this.isTimeout = options?.isTimeout === true
  }
}

/** HTTP 2xx, но тело `null` / пустое — типично для middleware без сериализации UniData в ответе. */
export class AttachmentUploadEmptyResponseError extends Error {
  readonly httpStatus: number

  constructor(httpStatus: number) {
    super(
      'Сервер принял файл, но не вернул UniData строки вложения.',
    )
    this.name = 'AttachmentUploadEmptyResponseError'
    this.httpStatus = httpStatus
  }
}

/** `true`, если после `unwrapPlatformJsonPayload` в теле нет данных строки (null, `""`, `"null"`). */
export function isEmptyUploadResponsePayload(payload: unknown): boolean {
  const unwrapped = unwrapPlatformJsonPayload(payload)
  if (unwrapped == null) return true
  if (unwrapped === '') return true
  if (typeof unwrapped === 'string') {
    const t = unwrapped.trim()
    return t === '' || t === 'null'
  }
  return false
}

function rejectInvalidUploadResponsePayload(payload: unknown, status?: number): void {
  if (isMiddleErrorResponse(payload)) {
    throw new AttachmentUploadError(getMiddleErrorMessage(payload), { status })
  }
  if (!isUniDataResponseDocument(payload)) {
    throw new AttachmentUploadError(getNonUniDataUploadResponseMessage(payload), { status })
  }
}

/** Сообщение для toast/UI по ошибке upload; для отмены (`ERR_CANCELED`) — пустая строка. */
export function formatAttachmentUploadError(error: unknown): string {
  if (error instanceof AttachmentUploadEmptyResponseError) {
    return 'Файл отправлен, но сервер не вернул данные строки. Повторите или обновите форму.'
  }
  if (error instanceof AttachmentUploadError) {
    if (error.isPayloadTooLarge) {
      return 'Файл слишком большой для загрузки (413).'
    }
    if (error.isTimeout) {
      return 'Превышено время ожидания загрузки файла.'
    }
    return error.message || 'Не удалось загрузить файл.'
  }
  if (error && typeof error === 'object' && 'code' in error && (error as { code: string }).code === 'ERR_CANCELED') {
    return ''
  }
  return 'Не удалось загрузить файл.'
}

/** Отмена пользователем: axios `ERR_CANCELED` или `DOMException` с именем `AbortError`. */
export function isAttachmentUploadAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true
  }
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return (error as { code: unknown }).code === 'ERR_CANCELED'
  }
  return false
}

/**
 * Асинхронная загрузка одного файла (axios, прогресс, `AbortSignal`).
 *
 * @returns поля строки после `parseAttachmentUploadResponse` — для `mergeUploadedRowResponse`
 */
export async function uploadAttachmentFile(params: {
  file: File
  elementId: string | number
  elementName: string
  session: PlatformFileSessionContext
  existingFields?: Record<string, unknown>
  signal?: AbortSignal
  onProgress?: (percent: number) => void
}): Promise<Record<string, unknown>> {
  const request = buildAttachmentUploadRequest({
    elementId: Number(params.elementId),
    session: params.session,
    file: params.file,
    elementName: params.elementName,
    existingFields: params.existingFields,
  })

  const formData = new FormData()
  appendAttachmentUploadMultipart(formData, request, params.file)

  try {
    const response = await axiosInstance({
      ...api.platform.upload,
      data: formData,
      signal: params.signal,
      timeout: UPLOAD_TIMEOUT_MS,
      responseType: 'json',
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
      onUploadProgress: (event) => {
        if (!event.total || !params.onProgress) return
        params.onProgress(Math.round((event.loaded * 100) / event.total))
      },
    })

    const status = response.status ?? 200
    const payload = unwrapPlatformJsonPayload(response.data)

    if (isEmptyUploadResponsePayload(payload)) {
      if (status >= 200 && status < 300) {
        throw new AttachmentUploadEmptyResponseError(status)
      }
      throw new AttachmentUploadError('Пустой ответ сервера при загрузке.', { status })
    }

    rejectInvalidUploadResponsePayload(payload, status)

    const row = parseAttachmentUploadResponse(payload)
    if (!row) {
      const hint =
        payload && typeof payload === 'object'
          ? ` (ключи: ${Object.keys(payload as object).join(', ')})`
          : ''
      console.warn('[FILEATTACHMENTS] upload response parse failed', payload)
      throw new AttachmentUploadError(
        `Ответ загрузки не содержит данных строки вложения${hint}.`,
      )
    }
    return row
  } catch (error: unknown) {
    if (
      isAttachmentUploadAbortError(error) ||
      error instanceof AttachmentUploadEmptyResponseError
    ) {
      throw error
    }
    const status = (error as { response?: { status?: number } })?.response?.status
    const isTimeout =
      (error && typeof error === 'object' && 'code' in error && (error as { code: string }).code === 'ECONNABORTED') ||
      false
    if (status === 413) {
      throw new AttachmentUploadError('Файл слишком большой для загрузки.', {
        status,
        isPayloadTooLarge: true,
      })
    }
    if (status === 403) {
      throw new AttachmentUploadError(
        'Доступ запрещён при загрузке файла (403). Обновите страницу или войдите снова.',
        { status },
      )
    }
    if (isTimeout) {
      throw new AttachmentUploadError('Превышено время ожидания загрузки.', { isTimeout: true })
    }
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      (error instanceof Error ? error.message : undefined)
    throw new AttachmentUploadError(message || 'Ошибка загрузки файла.', { status })
  }
}

/**
 * Синхронный POST multipart на `/platform/upload` (блокирующий XHR для WS getFullData).
 */
function postAttachmentUploadSync(
  formData: FormData,
  errorContext: string,
  options?: { allowEmptyResponse?: boolean },
): Record<string, unknown> | null {
  const xhr = new XMLHttpRequest()
  xhr.open('POST', getPlatformUploadUrl(), false)
  xhr.withCredentials = true
  xhr.setRequestHeader('Accept', 'application/json')
  applyWorkflowAuthHeaders((name, value) => {
    xhr.setRequestHeader(name, value)
  })
  xhr.send(formData)

  if (xhr.status === 0) {
    throw new AttachmentUploadError(
      `Сетевая ошибка при синхронной загрузке (${errorContext}, HTTP 0).`,
    )
  }

  if (xhr.status < 200 || xhr.status >= 300) {
    if (xhr.status === 403) {
      console.warn('[attachments:upload] HTTP 403', {
        response: xhr.responseText?.slice(0, 500),
        hasCsrf: Boolean(document.cookie.match(/CSRF-TOKEN=/i)),
        hasAuth: Boolean(localStorage.getItem('auth_token') || document.cookie.match(/X-AUTH-TOKEN=/i)),
      })
    }
    throw new AttachmentUploadError(
      xhr.status === 403
        ? 'Доступ запрещён при загрузке файла (403). Обновите страницу или войдите снова.'
        : `Ошибка загрузки файла (HTTP ${xhr.status}).`,
      {
        status: xhr.status,
        isPayloadTooLarge: xhr.status === 413,
      },
    )
  }

  let parsed: unknown = null
  const responseText = xhr.responseText
  if (responseText) {
    try {
      parsed = JSON.parse(responseText)
    } catch {
      throw new AttachmentUploadError('Некорректный JSON в ответе upload.')
    }
  }
  const payload = unwrapPlatformJsonPayload(parsed)

  if (isEmptyUploadResponsePayload(payload)) {
    if (options?.allowEmptyResponse) {
      return null
    }
    console.warn('[FILEATTACHMENTS] upload empty response', {
      context: errorContext,
      status: xhr.status,
      responseLength: responseText?.length ?? 0,
      responsePreview: responseText?.slice(0, 200) ?? '',
    })
    throw new AttachmentUploadEmptyResponseError(xhr.status)
  }

  rejectInvalidUploadResponsePayload(payload, xhr.status)

  const row = parseAttachmentUploadResponse(payload)
  if (!row) {
    throw new AttachmentUploadError('Ответ загрузки не содержит данных строки вложения.')
  }
  return row
}

/**
 * Синхронная загрузка (блокирующий XHR) — перед ответом WS `getData` / `getFullData`.
 *
 * Использует `applyWorkflowAuthHeaders` и `withCredentials` (как axios).
 * Блокирует главный поток до завершения запроса.
 *
 * В синхронном XHR нельзя задавать `responseType` (кроме пустого/`text`) — ответ разбирается из `responseText`.
 */
export function uploadAttachmentFileSync(params: {
  file: File
  elementId: string | number
  elementName: string
  session: PlatformFileSessionContext
  existingFields?: Record<string, unknown>
}): Record<string, unknown> {
  const request = buildAttachmentUploadRequest({
    elementId: Number(params.elementId),
    session: params.session,
    file: params.file,
    elementName: params.elementName,
    existingFields: params.existingFields,
  })

  const formData = new FormData()
  appendAttachmentUploadMultipart(formData, request, params.file)

  const row = postAttachmentUploadSync(formData, 'file')
  if (row == null) {
    throw new AttachmentUploadEmptyResponseError(200)
  }
  return row
}

/** Синхронный promote DS-staging → платформа: multipart без `file`, `storage_uri` в `currentData`. */
export function uploadAttachmentR7PromoteSync(params: {
  elementId: string | number
  elementName: string
  session: PlatformFileSessionContext
  existingFields?: Record<string, unknown>
  item?: FileItem
}): Record<string, unknown> | null {
  const baseFields = { ...(params.existingFields ?? {}) }
  const elementId = Number(params.elementId)
  let effectiveUri: string | null = null
  let documentSessionId: string | null = null
  if (params.item && Number.isFinite(elementId) && elementId > 0) {
    try {
      const snapshot = getDsAttachmentStore(elementId).getSnapshot(params.item)
      effectiveUri = snapshot?.storageUri?.trim() ?? null
      documentSessionId = snapshot?.documentSessionId?.trim() ?? null
    } catch {
      effectiveUri = null
      documentSessionId = null
    }
  }
  const fieldExtras: Record<string, { type: string; value: string }> = {}
  if (effectiveUri) {
    fieldExtras[IKey.STORAGE_URI] = { type: 'String', value: effectiveUri }
  }
  if (documentSessionId) {
    fieldExtras[MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD] = { type: 'String', value: documentSessionId }
  }
  const fields = { ...baseFields, ...fieldExtras }
  const storageUri = String(
    getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS) ?? '',
  ).trim()
  if (!storageUri && !documentSessionId) {
    throw new AttachmentUploadError(
      'Нет данных DS-снимка для promote после Р7. Дождитесь сохранения в редакторе (callback) или откройте файл в Р7 снова.',
    )
  }

  const request = buildAttachmentR7PromoteUploadRequest({
    elementId: Number(params.elementId),
    session: params.session,
    elementName: params.elementName,
    existingFields: fields,
  })

  const formData = new FormData()
  formData.append(
    'request',
    new Blob([JSON.stringify(request)], { type: 'application/json' }),
  )

  return postAttachmentUploadSync(formData, 'R7 promote', { allowEmptyResponse: true })
}

/** Upload перед getFullData: локальный File или R7 promote (storage_uri, без file). */
export function uploadAttachmentItemSync(params: {
  item: { file?: File; fields?: Record<string, unknown> }
  elementId: string | number
  elementName: string
  session: PlatformFileSessionContext
}): Record<string, unknown> | null {
  const item = params.item as FileItem
  const fields = (params.item.fields ?? {}) as Record<string, unknown>
  const elementId = Number(params.elementId)

  if (resolveAttachmentUploadSyncMode(item, elementId) === 'r7_promote') {
    return uploadAttachmentR7PromoteSync({
      elementId: params.elementId,
      elementName: params.elementName,
      session: params.session,
      existingFields: fields,
      item,
    })
  }

  if (!(params.item.file instanceof File)) {
    throw new AttachmentUploadError('Нет файла для загрузки вложения.')
  }
  return uploadAttachmentFileSync({
    file: params.item.file,
    elementId: params.elementId,
    elementName: params.elementName,
    session: params.session,
    existingFields: fields,
  })
}
